import { Formik, FormikConfig } from 'formik';
import React from 'react';

import { Box, Divider, Paper, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useIsMobile } from '@uplift/hooks';

import { Button, ButtonProps } from '../Button';
import { StickyStackItem } from '../Layout';
import { Loader } from '../Loader';
import { useEditableSectionsContext, useSectionEditRow } from './editable-sections.context';
import { EditableSectionRowProps } from './EditableSectionRow.component';
import {
  EditableSectionRowFormWrapper, EditableSectionRowFormWrapperProps
} from './EditableSectionRowFormWrapper.component';

type EditableSectionContainerEditProps = {
  sectionEditKey: string;
  rowFormWrapperProps?: Partial<Omit<EditableSectionRowFormWrapperProps, 'hideDesktopActions' | 'onCancel' | 'children'>>;
  disableFixedEditAction?: boolean;
} | {
  sectionEditKey?: never;
  rowFormWrapperProps?: never;
  disableFixedEditAction?: never;
};

export type EditableSectionContainerProps = {
  formikConfigs?: Record<string, (() => FormikConfig<any>) | FormikConfig<any>>;
  title: string;
  children?: React.ReactElement<EditableSectionRowProps>[] | React.ReactElement<EditableSectionRowProps>;
  rowDividers?: boolean;
  headerVariant?: 'inside' | 'outside' | 'archived' | 'admin';
  headerClassName?: string;
  headerSticky?: boolean;
  headerStickyOrder?: number;
  editButtonText?: string;
  editButtonProps?: Omit<ButtonProps, 'onClick'>;
  hideEditButton?: boolean;
  subtitle?: string;
  actions?: React.ReactNode;
  loading?: boolean;
  loaderHeight?: string;
  elevation?: number;
} & EditableSectionContainerEditProps;

const useStyles = makeStyles()((theme) => ({
  rowDividers: {
    '& *[role="row"]:not(:last-of-type)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    }
  },
  insideTitleContainer: {
    padding: theme.spacing(2, 2, 2, 3),
    minHeight: '64px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `2px solid ${theme.palette.divider}`,
    gap: theme.spacing(4)
  },
  archivedTitle: {
    alignItems: 'center',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  disableTransition: {
    transition: 'none',
    borderBottomLeftRadius: '0px',
    borderBottomRightRadius: '0px'

  }
}));

export const EditableSectionContainer: React.FC<EditableSectionContainerProps> = ({
  children,
  title,
  rowDividers = false,
  formikConfigs,
  sectionEditKey,
  headerVariant = 'outside',
  editButtonText = 'Edit',
  subtitle,
  actions,
  loading = false,
  loaderHeight = '64px',
  editButtonProps,
  rowFormWrapperProps,
  disableFixedEditAction,
  hideEditButton,
  headerClassName,
  headerSticky,
  headerStickyOrder,
  elevation = 4,
}) => {
  const { onEditRow, isEditRow, onCancelEdit } = useSectionEditRow(sectionEditKey);
  const { classes, cx } = useStyles();
  const isMobile = useIsMobile('sm');

  const mobileBorder = isMobile ? <Divider /> : null;

  const lastEditableRowIndex = React.useMemo(() => {
    let editableRowIndex = 0;

    children && React.Children.forEach<React.ReactElement<EditableSectionRowProps>>(children, (child, index) => {
      if (child?.props.kind === 'editable-row') {
        editableRowIndex = index;
      }
    });

    return editableRowIndex;
  }, [ children ]);

  const showEditButton = !!sectionEditKey && !isEditRow && (!isMobile || disableFixedEditAction) && !hideEditButton;
  const editButton = showEditButton
    ? (
      <Button
        variant="contained"
        color="secondary"
        onClick={onEditRow}
        testId="editable-section-edit-button"
        {...editButtonProps}
      >
        {editButtonText}
      </Button>
    ) : null;

  return (
    <EditableSectionContainerFormWrapper formikConfigs={formikConfigs}>
      <Box className={cx({ [classes.rowDividers]: rowDividers && !(sectionEditKey && isEditRow) })}>

        {headerVariant === 'outside' && (
          <EditableSectionContainerHeaderWrapper sticky={headerSticky} order={headerStickyOrder}>
            <Box
              pb={{ xs: 1, sm: 2 }}
              px={{ xs: 2, sm: 0 }}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              className={headerClassName}
            >
              <Typography variant="h4" data-testid="editable-section-title-outside">{title}</Typography>
              <Box className={classes.actions}>
                {actions}
                {editButton}
              </Box>
            </Box>
          </EditableSectionContainerHeaderWrapper>
        )}

        {headerVariant === 'admin' && (
          <Paper elevation={isMobile ? 0 : elevation} square={isMobile} className={classes.disableTransition}>

            <EditableSectionContainerHeaderWrapper sticky={headerSticky} order={headerStickyOrder} >
              <Box
                style={{ padding: '16px 24px 16px 24px' }}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                className={headerClassName}
              >
                <Box>
                  <Typography variant="h4" data-testid="editable-section-title-admin">{title}</Typography>
                </Box>
                <Box className={classes.actions}>
                  {actions}
                  {editButton}
                </Box>
              </Box>
            </EditableSectionContainerHeaderWrapper>
          </Paper>
        )}

        {headerVariant === 'archived' && (
          <EditableSectionContainerHeaderWrapper sticky={headerSticky} order={headerStickyOrder}>
            <Box
              pb={{ xs: 1, sm: 2 }}
              px={{ xs: 2, sm: 0 }}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              className={headerClassName}
            >
              <Typography variant="overline" color="textSecondary" data-testid="editable-section-title-archived">{title}</Typography>
            </Box>
          </EditableSectionContainerHeaderWrapper>
        )}

        {headerVariant === 'inside' && isMobile &&  (
          <EditableSectionContainerHeaderWrapper sticky={headerSticky} order={headerStickyOrder}>
            <Box mb={1} px={{ xs: 2, sm: 0 }} className={headerClassName}>
              <Box>
                <Typography variant="overline" color="secondary">{title}</Typography>
                <Typography variant="body2" display="block">{subtitle}</Typography>
              </Box>
              <Box className={classes.actions}>
                {actions}
              </Box>
            </Box>
          </EditableSectionContainerHeaderWrapper>
        )
        }

        <Box >
          {mobileBorder}
          {headerVariant === 'inside' && mobileBorder}
          <Paper elevation={isMobile ? 0 : elevation} square={isMobile} className={classes.disableTransition}>

            {headerVariant === 'inside' && !isMobile && (
              <EditableSectionContainerHeaderWrapper sticky={headerSticky} order={headerStickyOrder}>
                <Box className={cx(classes.insideTitleContainer, headerClassName)}>
                  <Box>
                    <Typography variant="overline" color="secondary">{title}</Typography>
                    <Typography variant="body2" display="block">{subtitle}</Typography>
                  </Box>
                  <Box className={classes.actions}>
                    {actions}
                    {editButton}
                  </Box>
                </Box>
              </EditableSectionContainerHeaderWrapper>
            )}

            {loading && <Loader color="primary" height={loaderHeight} />}

            {React.Children.map<React.ReactNode, React.ReactElement<EditableSectionRowProps>>(children ?? [], (child, index) => {
              if (!React.isValidElement(child)) {
                return null;
              }

              if (sectionEditKey && isEditRow && child.props.kind !== 'editable-row' && !child.props.showOnSectionEdit && !isMobile) {
                return null;
              }

              return React.cloneElement(child, {
                ...child.props,
                rowKey: sectionEditKey ?? child.props.rowKey,
                hideEditButton: !!sectionEditKey || child.props.hideEditButton,
                disabledMobileFormModal: !!sectionEditKey || child.props.disabledMobileFormModal,
                rowFormWrapperProps: {
                  ...child.props.rowFormWrapperProps,
                  ...rowFormWrapperProps,
                  hideDesktopActions: !!sectionEditKey && index !== lastEditableRowIndex,
                }
              });
            })}

            {isMobile && isEditRow && !!sectionEditKey && (
              <EditableSectionRowFormWrapper onCancel={onCancelEdit} rowLabel={title} {...rowFormWrapperProps}>

                {React.Children.count(children ?? []) === 1 && (
                  <Box height="100%">
                    {React.Children.map<React.ReactNode, React.ReactElement<EditableSectionRowProps>>(children ?? [], (child) => {
                      if (!React.isValidElement(child) || (isEditRow && child.props.kind !== 'editable-row')) {
                        return null;
                      }

                      return child.props.form;
                    })}
                  </Box>
                )}

                {React.Children.count(children ?? []) > 1 && (
                  <Box display="flex" flexDirection="column" gap="1.5rem">
                    {React.Children.map<React.ReactNode, React.ReactElement<EditableSectionRowProps>>(children ?? [], (child) => {
                      if (!React.isValidElement(child) || (isEditRow && child.props.kind !== 'editable-row')) {
                        return null;
                      }

                      return <Box flex={1}>{child.props.form}</Box>;
                    })}
                  </Box>
                )}
              </EditableSectionRowFormWrapper>
            )}

            {isMobile && !!sectionEditKey && !disableFixedEditAction && (
              <EditableSectionContainerFixedEditAction onClick={onEditRow} editButtonText={editButtonText} />
            )}

          </Paper>
          {mobileBorder}
        </Box>
      </Box>
    </EditableSectionContainerFormWrapper>
  );
};

export type EditableSectionContainerHeaderWrapperProps = {
  children: React.ReactNode;
  sticky: EditableSectionContainerProps['headerSticky'];
  order: EditableSectionContainerProps['headerStickyOrder'];
};

export const EditableSectionContainerHeaderWrapper: React.FC<EditableSectionContainerHeaderWrapperProps> = ({ children, sticky, order }) => {
  if (sticky && React.isValidElement(children)) {
    return (
      <StickyStackItem placement="top" order={order ?? 0}>
        {children}
      </StickyStackItem>
    );
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

export type EditableSectionContainerFormWrapperProps = Pick<EditableSectionContainerProps, 'formikConfigs'> & {
  children: React.ReactElement<EditableSectionContainerProps>;
};

export const EditableSectionContainerFormWrapper: React.FC<EditableSectionContainerFormWrapperProps> = ({ children, formikConfigs }) => {
  const editableSectionsState = useEditableSectionsContext();

  const formikProps = React.useMemo(() => {
    const formik = editableSectionsState?.editKey && formikConfigs ? formikConfigs[editableSectionsState.editKey] : null;

    return typeof formik === 'function' ? formik() : formik;
  }, [ editableSectionsState?.editKey, formikConfigs ]);

  if (formikProps) {
    return <Formik {...formikProps} enableReinitialize>{children}</Formik>;
  }

  return children;
};

export type EditableSectionContainerFixedEditActionProps = {
  onClick: () => void;
  editButtonText?: string;
  testId?: string;
};

export const EditableSectionContainerFixedEditAction: React.VFC<EditableSectionContainerFixedEditActionProps> = ({ onClick, editButtonText = 'Edit', testId='editable-section-fixed-edit-button' }) => {
  const { classes } = useMobileFixedEditActionStyles();

  return (
    <Box className={classes.container}>
      <Button
        variant="contained"
        color="secondary"
        size="large"
        fullWidth
        onClick={onClick}
        testId={testId}
      >
        {editButtonText}
      </Button>
    </Box>
  );
};

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useMobileFixedEditActionStyles = makeStyles()((theme) => ({
  container: {
    position: 'fixed',
    right: 0,
    bottom: 0,
    left: 0,
    padding: theme.spacing(2),
    background: theme.palette.background.paper,
    boxShadow: `inset 0 1px 0 ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    zIndex: theme.zIndex.appBar
  }
}));
